import { Button } from "@designs-by-maryam/ui/components/button";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import { motion } from "motion/react";
import { useSiteContact } from "@/hooks/use-site-contact";
import { useSafeMotion } from "@/lib/motion";

type HomeContactSectionProps = {
	className?: string;
};

/**
 * HomeContactSection Component
 *
 * A concise contact section for the homepage that encourages visitors
 * to reach out without the full contact form complexity.
 *
 * Features:
 * - Quick contact info from CMS (email, phone, location)
 * - Static fallback when CMS unavailable
 * - Prominent CTA to full contact page
 * - Motion entrance animations
 * - Responsive layout
 */
export function HomeContactSection({ className }: HomeContactSectionProps) {
	const { slideUp, fadeIn, viewportOnce, shouldReduce } = useSafeMotion();
	const { data: contact } = useSiteContact();

	const contactInfo = [
		{
			icon: Mail,
			label: "Email",
			value: contact.email,
			href: contact.emailHref,
		},
		{
			icon: Phone,
			label: "Phone",
			value: contact.phone,
			href: contact.phoneHref,
		},
		{
			icon: MapPin,
			label: "Location",
			value: contact.location,
		},
	];

	return (
		<section
			aria-label="Contact Us"
			className={cn(
				"relative overflow-hidden bg-muted/10 py-16 md:py-24 dark:bg-muted/5",
				className,
			)}
		>
			{/* Decorative corner shapes */}
			<div
				aria-hidden="true"
				className="pointer-events-none absolute top-0 left-0 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/5 blur-3xl"
			/>
			<div
				aria-hidden="true"
				className="pointer-events-none absolute bottom-0 right-0 h-64 w-64 translate-x-1/2 translate-y-1/2 rounded-full bg-purple-500/3 blur-3xl"
			/>

			<div className="container relative mx-auto px-4 md:px-6">
				{/* FLIPPED LAYOUT: Cards left, Content right on desktop */}
				<div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
					{/* Left - Contact Info Cards (was Right) */}
					<motion.div
						className="order-2 space-y-4 lg:order-1"
						initial={fadeIn.initial}
						transition={{
							...fadeIn.transition,
							delay: shouldReduce ? 0 : 0.2,
						}}
						viewport={viewportOnce}
						whileInView={fadeIn.animate}
					>
						{/* Contact Cards - Upgraded styling */}
						<div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
							{contactInfo.map((item) => {
								const content = (
									<div
										className={cn(
											"group relative flex items-center gap-4 overflow-hidden rounded-2xl border-2 border-primary/20 bg-card/70 p-6 shadow-lg transition-all backdrop-blur-sm",
											item.href
												? "cursor-pointer hover:-translate-y-1 hover:shadow-xl"
												: "",
										)}
										key={item.label}
									>
										{/* Shimmer effect */}
										<div
											aria-hidden="true"
											className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 transition-all duration-700 group-hover:translate-x-full group-hover:opacity-100"
										/>
										{/* Icon container with glow effect */}
										<div className="relative">
											<div className="absolute inset-0 rounded-xl bg-primary/20 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100" />
											<div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary shadow-sm transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110">
												<item.icon className="h-5 w-5" />
											</div>
										</div>
										<div className="relative">
											<p className="font-medium text-foreground text-sm">
												{item.label}
											</p>
											<p className="text-muted-foreground text-sm">
												{item.value}
											</p>
										</div>
										{/* Bottom accent line */}
										<div
											aria-hidden="true"
											className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-primary to-primary/80 transition-all duration-500 group-hover:w-full"
										/>
									</div>
								);

								return item.href ? (
									<a
										className="block"
										href={item.href}
										key={item.label}
										rel="noopener noreferrer"
									>
										{content}
									</a>
								) : (
									content
								);
							})}
						</div>

						{/* Availability Note - Upgraded styling */}
						<div className="group relative overflow-hidden rounded-2xl border-2 border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5 p-6 shadow-lg dark:border-primary/30 dark:from-primary/15 dark:to-primary/8">
							{/* Shimmer effect */}
							<div
								aria-hidden="true"
								className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 transition-all duration-700 group-hover:translate-x-full group-hover:opacity-100"
							/>
							<p className="relative text-muted-foreground text-sm">
								<span className="font-medium text-foreground">
									Currently accepting new projects.
								</span>{" "}
								Response time is typically within 24-48 hours.
							</p>
						</div>
					</motion.div>

					{/* Right - Content (was Left) */}
					<motion.div
						className="order-1 space-y-8 text-center lg:order-2 lg:text-left"
						initial={slideUp.initial}
						transition={slideUp.transition}
						viewport={viewportOnce}
						whileInView={slideUp.animate}
					>
						<div className="space-y-4">
							<h2 className="font-bold text-3xl tracking-tight md:text-4xl lg:text-5xl">
								Let's Create Something Beautiful
							</h2>
							<p className="text-lg text-muted-foreground">
								Ready to transform your space? Whether you have a specific
								project in mind or just want to explore possibilities, I'd love
								to hear from you.
							</p>
						</div>

						{/* CTA Buttons */}
						<div className="flex flex-col gap-4 sm:flex-row sm:justify-center lg:justify-start">
							<Button asChild size="lg" variant="brand">
								<Link to="/contact">
									Start Your Project
									<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
								</Link>
							</Button>
							<Button asChild size="lg" variant="brand-outline">
								<Link to="/portfolio">View Portfolio</Link>
							</Button>
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}

export default HomeContactSection;
